import {
  REACT_APP_AUTH0_AUDIENCE,
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_SCOPES,
  REACT_APP_AUTH0_SIEMENS_SETTINGS,
  REACT_APP_IS_SIEMENS_BRANDING_URL,
} from 'utils/configs';

type SiemensAuth0Settings = {
  domain: string;
  audience: string;
  clientId: string;
  scope?: string;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * REACT_APP_AUTH0_SIEMENS_SETTINGS must be a JSON object using UPPER_SNAKE keys only.
 * Required string fields: REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_AUDIENCE, REACT_APP_AUTH0_CLIENT_ID.
 * Optional: REACT_APP_AUTH0_SCOPE (otherwise env AUTH0_SCOPE is used).
 */
const parseSiemensAuth0Settings = (): SiemensAuth0Settings | null => {
  const raw = REACT_APP_AUTH0_SIEMENS_SETTINGS?.trim();
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;

    const domain = parsed.REACT_APP_AUTH0_DOMAIN;
    const audience = parsed.REACT_APP_AUTH0_AUDIENCE;
    const clientId = parsed.REACT_APP_AUTH0_CLIENT_ID;

    if (
      typeof domain !== 'string' ||
      typeof audience !== 'string' ||
      typeof clientId !== 'string'
    ) {
      return null;
    }

    let scope: string | undefined;
    if (parsed.REACT_APP_AUTH0_SCOPE !== undefined) {
      if (typeof parsed.REACT_APP_AUTH0_SCOPE !== 'string') return null;
      scope = parsed.REACT_APP_AUTH0_SCOPE;
    }

    const out: SiemensAuth0Settings = {
      domain,
      audience,
      clientId,
    };
    if (scope !== undefined) out.scope = scope;
    return out;
  } catch {
    return null;
  }
};

export const resolveAuth0ProviderSettings = () => {
  const useSiemensSettings = REACT_APP_IS_SIEMENS_BRANDING_URL === 'true';
  const siemens = parseSiemensAuth0Settings();

  return {
    domain:
      (useSiemensSettings && siemens?.domain?.trim()) ||
      REACT_APP_AUTH0_DOMAIN ||
      '',
    audience:
      (useSiemensSettings && siemens?.audience?.trim()) ||
      REACT_APP_AUTH0_AUDIENCE ||
      '',
    clientId:
      (useSiemensSettings && siemens?.clientId?.trim()) ||
      REACT_APP_AUTH0_CLIENT_ID ||
      '',
    scope:
      (useSiemensSettings && siemens?.scope?.trim()) || REACT_APP_AUTH0_SCOPES,
  };
};
